/**
 * Accident / construction / closure pins plus NWS weather alerts.
 * Shared by COP and the Pathfinder navigator so one traffic toggle paints both.
 */
import React from 'react';
import { Marker } from 'react-map-gl';
import { Box, Tooltip, Typography } from '@mui/material';
import { Warning as WeatherAlertIcon } from '@mui/icons-material';
import type { TrafficIncident, WeatherAlert } from '../../services/areaIntelService';

const INCIDENT_COLOR = '#FF9800';
const ICON_SIZE = 22;

function IncidentGlyph({ type }: { type: string }) {
  const shadow = { filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.8))' } as const;
  switch (type) {
    case 'accident':
      return (
        <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill={INCIDENT_COLOR} style={shadow}>
          <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
        </svg>
      );
    case 'construction':
      return (
        <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill={INCIDENT_COLOR} style={shadow}>
          <path d="M19 12h-1.5l-1.5-6h-8L6.5 12H5l-2 8h18l-2-8zm-7-4l1 4h-2l1-4zm-4 4l.5-2h1l-.5 2H8zm4 6h-2v-2h2v2zm4-6h-.5l-.5-2h1l.5 2z" />
        </svg>
      );
    case 'congestion':
      return (
        <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill={INCIDENT_COLOR} style={shadow}>
          <path d="M20 10h-3V8.86c1.72-.45 3-2 3-3.86h-3V4c0-.55-.45-1-1-1H8c-.55 0-1 .45-1 1v1H4c0 1.86 1.28 3.41 3 3.86V10H4c0 1.86 1.28 3.41 3 3.86V15H4c0 1.86 1.28 3.41 3 3.86V20c0 .55.45 1 1 1h8c.55 0 1-.45 1-1v-1.14c1.72-.45 3-2 3-3.86h-3v-1.14c1.72-.45 3-2 3-3.86zm-8 8c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0-5c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0-5c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
        </svg>
      );
    case 'road_closure':
      return (
        <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill={INCIDENT_COLOR} style={shadow}>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11H7v-2h10v2z" />
        </svg>
      );
    case 'weather':
      return (
        <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill={INCIDENT_COLOR} style={shadow}>
          <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4 9.11 4 6.6 5.64 5.35 8.04 2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96zM19 18H6c-2.21 0-4-1.79-4-4s1.79-4 4-4h.71C7.37 7.69 9.48 6 12 6c3.04 0 5.5 2.46 5.5 5.5v.5H19c1.66 0 3 1.34 3 3s-1.34 3-3 3z" />
        </svg>
      );
    default:
      return (
        <svg width={ICON_SIZE} height={ICON_SIZE} viewBox="0 0 24 24" fill={INCIDENT_COLOR} style={shadow}>
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
      );
  }
}

export interface MapTrafficIncidentsProps {
  visible: boolean;
  incidents?: TrafficIncident[];
  weatherAlerts?: WeatherAlert[];
}

const MapTrafficIncidents: React.FC<MapTrafficIncidentsProps> = ({
  visible,
  incidents = [],
  weatherAlerts = [],
}) => {
  if (!visible) return null;

  return (
    <>
      {incidents.map((incident) => (
        <Marker
          key={`incident-${incident.id}`}
          longitude={incident.longitude}
          latitude={incident.latitude}
          anchor="center"
        >
          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="caption" fontWeight="bold" display="block">
                  {incident.type.replace('_', ' ').toUpperCase()}
                </Typography>
                <Typography variant="caption" display="block" sx={{ opacity: 0.9, textTransform: 'capitalize' }}>
                  Severity: {incident.severity}
                </Typography>
                {incident.road && (
                  <Typography variant="caption" display="block" sx={{ opacity: 0.8, mt: 0.5 }}>
                    {incident.road}
                  </Typography>
                )}
              </Box>
            }
            arrow
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'transform 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <IncidentGlyph type={incident.type} />
            </div>
          </Tooltip>
        </Marker>
      ))}
      {weatherAlerts.map((alert) => {
        if (alert.latitude == null || alert.longitude == null) return null;
        return (
          <Marker
            key={`weather-alert-${alert.id}`}
            longitude={alert.longitude}
            latitude={alert.latitude}
            anchor="center"
          >
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" fontWeight="bold" display="block">
                    {alert.event}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ opacity: 0.9, textTransform: 'capitalize' }}>
                    Severity: {alert.severity}
                  </Typography>
                  {alert.expires && (
                    <Typography variant="caption" display="block" sx={{ opacity: 0.8, mt: 0.5 }}>
                      Expires: {new Date(alert.expires).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              }
              arrow
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: '#03a9f4',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 0 10px rgba(3,169,244,0.85)',
                  border: '2px solid rgba(255,255,255,0.85)',
                }}
              >
                <WeatherAlertIcon fontSize="small" />
              </div>
            </Tooltip>
          </Marker>
        );
      })}
    </>
  );
};

export default MapTrafficIncidents;
